import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageUp, Trash2 } from 'lucide-react';
import { api, ApiError, errorMessage } from '@/lib/api';
import type { Program } from '@/lib/types';
import { fmtDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox, Field, Input, Textarea } from '@/components/ui/field';
import { Alert, Card, CardHeader, Spinner } from '@/components/ui/misc';
import { useToast } from '@/components/ui/toast';

interface Settings {
  name: string;
  clientName: string;
  clientShortName: string;
  consultantName: string;
  startDate: string;
  estimatedEndDate: string;
  healthNarrative: string | null;
  requireMfaForAll: boolean;
  logo: { mime: string; version: number } | null;
}

export function ProgramSettings({ program }: { program: Program }) {
  const base = `/api/programs/${program.id}/admin`;
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: ['admin', program.id, 'settings'],
    queryFn: () => api.get<Settings>(`${base}/settings`),
  });
  const [form, setForm] = useState<Settings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (q.data) setForm(q.data);
  }, [q.data]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['admin', program.id] });
    void qc.invalidateQueries({ queryKey: ['program', program.id] });
  };

  if (!form) return <Spinner />;

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.patch(`${base}/settings`, {
        name: form!.name,
        clientName: form!.clientName,
        clientShortName: form!.clientShortName,
        healthNarrative: form!.healthNarrative,
        requireMfaForAll: form!.requireMfaForAll,
      });
      toast.ok('Program settings saved.');
      refresh();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function upload(f: File) {
    try {
      const res = await fetch(`${base}/logo`, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'Content-Type': f.type, 'X-HELM': '1' },
        body: f,
      });
      if (!res.ok) {
        const e = (await res.json().catch(() => ({}))).error ?? {};
        throw new ApiError(res.status, e.code ?? 'ERROR', e.message ?? 'Upload failed.');
      }
      toast.ok('Logo updated.');
      refresh();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  const set = <K extends keyof Settings>(k: K, v: Settings[K]) => setForm({ ...form!, [k]: v });
  const logoUrl = form.logo ? `/api/programs/${program.id}/logo?v=${form.logo.version}` : null;

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
      <Card>
        <CardHeader
          title="Program"
          description={`Window ${fmtDate(form.startDate)} to ${fmtDate(form.estimatedEndDate)}. Dates move with phase reforecasts in the Tracker.`}
        />
        <form onSubmit={save} className="flex flex-col gap-4 px-5 py-4">
          {error && <Alert tone="bad">{error}</Alert>}
          <Field label="Program name" htmlFor="s-name">
            <Input id="s-name" required value={form.name} onChange={(e) => set('name', e.target.value)} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-[1fr_140px]">
            <Field label="Client name" htmlFor="s-client">
              <Input
                id="s-client"
                required
                value={form.clientName}
                onChange={(e) => set('clientName', e.target.value)}
              />
            </Field>
            <Field label="Short name" htmlFor="s-short" hint="Used in labels.">
              <Input
                id="s-short"
                required
                maxLength={12}
                value={form.clientShortName}
                onChange={(e) => set('clientShortName', e.target.value)}
              />
            </Field>
          </div>
          <Field
            label="Health narrative"
            htmlFor="s-narr"
            hint="One line for the program health banner on the Executive tab. 280 characters."
          >
            <Textarea
              id="s-narr"
              maxLength={280}
              rows={2}
              value={form.healthNarrative ?? ''}
              onChange={(e) => set('healthNarrative', e.target.value)}
            />
          </Field>
          <Checkbox
            id="s-mfa"
            checked={form.requireMfaForAll}
            onChange={(v) => set('requireMfaForAll', v)}
            label="Require two-factor authentication for every role"
            hint="Admins always need it. When on, executives and contributors set it up at their next sign-in."
          />
          <div>
            <Button type="submit" loading={busy}>
              Save changes
            </Button>
          </div>
        </form>
      </Card>

      <Card>
        <CardHeader
          title="Client logo"
          description="Shown on the program header and report covers, colours unaltered."
        />
        <div className="flex flex-col gap-3 px-5 py-4">
          {logoUrl ? (
            <div className="grid grid-cols-2 gap-2">
              <div className="flex h-24 items-center justify-center rounded-md border border-border bg-white p-2">
                <img src={logoUrl} alt="Logo on a light header" className="max-h-full max-w-full" />
              </div>
              <div className="flex h-24 items-center justify-center rounded-md border border-border bg-[#14161a] p-2">
                <span className="rounded-md bg-white p-1.5">
                  <img src={logoUrl} alt="Logo on a dark header" className="max-h-14 max-w-full" />
                </span>
              </div>
            </div>
          ) : (
            <p className="rounded-md border border-dashed border-border-strong px-3 py-6 text-center text-sm text-muted">
              No logo yet.
            </p>
          )}
          <input
            ref={file}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void upload(f);
              e.target.value = '';
            }}
          />
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => file.current?.click()}>
              <ImageUp /> {logoUrl ? 'Replace' : 'Upload'}
            </Button>
            {logoUrl && (
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  await api.delete(`${base}/logo`).catch((e) => toast.error(errorMessage(e)));
                  refresh();
                }}
              >
                <Trash2 /> Remove
              </Button>
            )}
          </div>
          <p className="text-xs text-muted">
            PNG, JPG, WebP or SVG, up to 1 MB. A high-resolution PNG or an SVG prints best on report covers.
          </p>
        </div>
      </Card>
    </div>
  );
}
